<template>
  <button
    :type="type"
    :disabled="disabled || loading"
    :class="buttonClasses"
    @click="handleClick"
  >
    <span v-if="loading" class="app-button-spinner" aria-hidden="true">
      <svg class="animate-spin" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
        <path
          class="opacity-75"
          fill="currentColor"
          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
        />
      </svg>
    </span>

    <slot v-if="!loading" />
  </button>
</template>

<script setup lang="ts">
import { computed } from 'vue'

/**
 * AppButton — mirrors core/ui/buttons/AppButton.kt.
 *
 *   primary   — filled ink black, white text (default CTA)
 *   secondary — surface fill, hairline border, ink-900 text
 *   ghost     — transparent, ink-900 text
 *   text      — transparent, ink-900 text, no padding container styling
 *   danger    — semantic danger fill, white text
 *   ai        — palette purple fill, white text
 *
 * Sizes match Android's controlSm/Md/Lg = 32 / 40 / 48dp.
 */

interface Props {
  variant?: 'primary' | 'secondary' | 'ghost' | 'text' | 'danger' | 'ai'
  size?: 'sm' | 'md' | 'lg'
  disabled?: boolean
  loading?: boolean
  fullWidth?: boolean
  type?: 'button' | 'submit' | 'reset'
}

const props = withDefaults(defineProps<Props>(), {
  variant: 'primary',
  size: 'md',
  disabled: false,
  loading: false,
  fullWidth: false,
  type: 'button',
})

const emit = defineEmits<{
  click: [event: MouseEvent]
}>()

const buttonClasses = computed(() => [
  'app-button',
  `app-button-${props.variant}`,
  `app-button-${props.size}`,
  {
    'app-button-full-width': props.fullWidth,
    'app-button-loading': props.loading,
  },
])

const handleClick = (event: MouseEvent) => {
  if (!props.disabled && !props.loading) {
    emit('click', event)
  }
}
</script>

<style scoped>
.app-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-family: var(--font-sans);
  font-weight: var(--weight-medium);
  letter-spacing: 0;
  border-radius: var(--radius-md);
  border: 1px solid transparent;
  cursor: pointer;
  transition:
    background-color var(--motion-fast),
    color var(--motion-fast),
    border-color var(--motion-fast),
    box-shadow var(--motion-fast);
  white-space: nowrap;
  user-select: none;
  outline: none;
}

.app-button:focus-visible {
  outline: 2px solid var(--focus-ring);
  outline-offset: 2px;
}

.app-button:disabled {
  cursor: not-allowed;
  opacity: 0.5;
}

/* Primary — ink black */
.app-button-primary {
  background: var(--ink-black);
  color: var(--ink-white);
}
.app-button-primary:hover:not(:disabled) { background: var(--ink-900); }
.app-button-primary:active:not(:disabled) { background: var(--ink-800); }

/* Secondary — surface + hairline */
.app-button-secondary {
  background: var(--surface);
  color: var(--ink-900);
  border-color: var(--outline-variant);
}
.app-button-secondary:hover:not(:disabled) {
  background: var(--surface-muted);
  border-color: var(--outline);
}
.app-button-secondary:active:not(:disabled) {
  background: var(--surface-variant);
}

/* Ghost — transparent */
.app-button-ghost {
  background: transparent;
  color: var(--ink-900);
}
.app-button-ghost:hover:not(:disabled) {
  background: var(--surface-muted);
}
.app-button-ghost:active:not(:disabled) {
  background: var(--surface-variant);
}

/* Text — pure text link, smaller padding feel */
.app-button-text {
  background: transparent;
  color: var(--ink-900);
  padding-left: 0;
  padding-right: 0;
}
.app-button-text:hover:not(:disabled) {
  color: var(--ink-black);
  text-decoration: underline;
  text-underline-offset: 3px;
}

/* Danger */
.app-button-danger {
  background: var(--danger-ink);
  color: var(--ink-white);
}
.app-button-danger:hover:not(:disabled) { filter: brightness(0.95); }
.app-button-danger:active:not(:disabled) { filter: brightness(0.9); }

/* AI */
.app-button-ai {
  background: var(--ai-solid);
  color: var(--ink-white);
}
.app-button-ai:hover:not(:disabled) { filter: brightness(0.95); }
.app-button-ai:active:not(:disabled) { filter: brightness(0.9); }

/* Sizes — match controlSm / controlMd / controlLg on Android. */
.app-button-sm {
  height: var(--control-sm);
  padding: 0 12px;
  font-size: var(--text-label);
  line-height: var(--text-label-lh);
}
.app-button-sm :deep(svg) { width: var(--icon-xs); height: var(--icon-xs); }

.app-button-md {
  height: var(--control-md);
  padding: 0 16px;
  font-size: var(--text-body);
  line-height: var(--text-body-lh);
}
.app-button-md :deep(svg) { width: 16px; height: 16px; }

.app-button-lg {
  height: var(--control-lg);
  padding: 0 24px;
  font-size: var(--text-title-sm);
  line-height: var(--text-title-sm-lh);
}
.app-button-lg :deep(svg) { width: var(--icon-sm); height: var(--icon-sm); }

.app-button-full-width { width: 100%; }

.app-button-spinner { display: inline-flex; }
.app-button-spinner svg { width: 16px; height: 16px; }

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
.animate-spin { animation: spin 1s linear infinite; }
</style>
